/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree and displays a fallback UI.
 * Provides error reporting and recovery options.
 */

import { Component } from "react";
import ErrorDisplay from "./ui/ErrorDisplay";
import { config } from "../config";
import { trackError } from "../utils/analytics";

/**
 * Error Boundary Class Component
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2),
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    this.logError(error, errorInfo);

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });
  }

  logError = (error, errorInfo) => {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorId: this.state.errorId,
    };

    // Track error with Segment
    trackError("React Error Boundary", window.location.pathname, error.message);

    // Log to console in development
    if (config.app.environment === "development") {
      console.group("🚨 Error Boundary Caught Error");
      console.error("Error:", error);
      console.error("Error Info:", errorInfo);
      console.error("Error Data:", errorData);
      console.groupEnd();
    }

    // In production, you might want to send this to an error reporting service
    // Example: Sentry, LogRocket, Bugsnag, etc.
    if (config.app.environment === "production" && this.props.onError) {
      try {
        this.props.onError(errorData);
      } catch (reportingError) {
        console.error("Error reporting failed:", reportingError);
      }
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided by parent
      if (this.props.fallback) {
        if (typeof this.props.fallback === "function") {
          return this.props.fallback(this.state.error, this.handleRetry);
        }
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-lg w-full">
            <ErrorDisplay
              error={this.state.error}
              title="Something went wrong"
              message="An unexpected error occurred. Please try refreshing the page or contact support if the problem persists."
              details={this.formatErrorDetails()}
              onRetry={this.handleRetry}
              retryText="Try Again"
              size="lg"
              className="mb-4"
            />

            <div className="flex justify-center space-x-4">
              <button
                onClick={this.handleReload}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Reload Page
              </button>

              {this.props.onReportError && (
                <button
                  onClick={() =>
                    this.props.onReportError(
                      this.state.error,
                      this.state.errorInfo
                    )
                  }
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Report Error
                </button>
              )}
            </div>

            {config.app.environment === "development" && (
              <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Development Info
                </h3>
                <div className="text-xs text-gray-700 font-mono whitespace-pre-wrap">
                  {this.state.error?.stack}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }

  formatErrorDetails = () => {
    if (!this.state.error || !this.state.errorInfo) return null;

    const details = [];

    if (this.state.errorId) {
      details.push(`Error ID: ${this.state.errorId}`);
    }

    details.push(`Timestamp: ${new Date().toISOString()}`);

    if (this.state.error.message) {
      details.push(`Message: ${this.state.error.message}`);
    }

    if (this.state.errorInfo.componentStack) {
      details.push(`Component Stack:${this.state.errorInfo.componentStack}`);
    }

    if (this.state.error.stack) {
      details.push(`Stack Trace:\n${this.state.error.stack}`);
    }

    return details.join("\n\n");
  };
}

/**
 * Higher-Order Component to wrap components with error boundary
 */
export function withErrorBoundary(Component, errorBoundaryProps = {}) {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

/**
 * Hook-based Error Boundary (for functional components)
 * Note: This is a workaround since hooks can't catch errors in event handlers
 */
export function useErrorHandler() {
  const handleError = (error, errorInfo = {}) => {
    // Create a custom error that will be caught by the nearest error boundary
    const errorBoundaryError = new Error(error.message || error);
    errorBoundaryError.stack = error.stack || error;
    errorBoundaryError.componentStack = errorInfo.componentStack || "";

    // Throw the error to be caught by error boundary
    throw errorBoundaryError;
  };

  return handleError;
}

/**
 * Async Error Boundary for handling async errors
 */
export class AsyncErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Async Error Boundary caught error:", error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      return (
        <ErrorDisplay
          error={this.state.error}
          title="Async Operation Failed"
          message="An error occurred during an asynchronous operation."
          onRetry={this.handleRetry}
          className="m-4"
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Specialized Error Boundaries
 */

/**
 * Route Error Boundary (for route-level error handling)
 */
export function RouteErrorBoundary({ children, routeName }) {
  return (
    <ErrorBoundary
      fallback={(error, retry) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <ErrorDisplay
            error={error}
            title={`Error in ${routeName || "page"}`}
            message="This page encountered an error. You can try again or return to the home page."
            onRetry={retry}
            size="lg"
          />
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Component Error Boundary (for component-level error handling)
 */
export function ComponentErrorBoundary({
  children,
  componentName,
  className = "",
}) {
  return (
    <ErrorBoundary
      fallback={(error, retry) => (
        <div className={`p-4 ${className}`}>
          <ErrorDisplay
            error={error}
            title={`${componentName || "Component"} Error`}
            message="This component encountered an error and couldn't render properly."
            onRetry={retry}
            size="sm"
          />
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Form Error Boundary (for form-level error handling)
 */
export function FormErrorBoundary({ children, onFormError }) {
  return (
    <ErrorBoundary
      fallback={(error, retry) => (
        <ErrorDisplay
          error={error}
          title="Form Error"
          message="The form encountered an error. Please check your input and try again."
          onRetry={retry}
          size="sm"
          className="mb-4"
        />
      )}
      onError={onFormError}
    >
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
